import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, apiError } from "../lib/api";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Loader2, UserCircle2, Save } from "lucide-react";

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user) return;
    setForm((prev) => ({
      ...prev,
      name: user.name || "",
      email: user.email || "",
      company: user.company || "",
    }));
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;

      const { data } = await api.put("/profile", payload);
      setUser(data.user);
      toast.success("Profile updated successfully");
      navigate(["admin", "super_admin", "technician"].includes(user?.role) ? "/admin" : "/dashboard");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || apiError(err.response?.data?.message) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto">
        <div className="mb-6 flex items-center gap-3">
          <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
            <UserCircle2 className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="font-heading font-black tracking-tighter text-4xl">Profile</h1>
            <p className="text-muted-foreground mt-1">Update your personal information and password</p>
          </div>
        </div>

        <form onSubmit={submit} className="rounded-lg border border-border bg-card p-6 space-y-5" data-testid="profile-form">
          {error && <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2">{error}</div>}

          <div className="space-y-2">
            <Label htmlFor="name">Full name</Label>
            <Input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="profile-name" />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="profile-email" />
          </div>

          <div className="space-y-2">
            <Label htmlFor="company">Company</Label>
            <Input id="company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="profile-company" />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">New password</Label>
            <Input id="password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Leave blank to keep current password" minLength={6} data-testid="profile-password" />
          </div>

          <Button type="submit" className="gap-2" disabled={loading} data-testid="profile-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
          </Button>
        </form>
      </div>
    </Layout>
  );
}
